import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '@/api/envelope';
import { ErrorState } from '@/components/ui/ErrorState';
import { FilterBar } from '@/components/ui/FilterBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { testIdAttr, testIdPart } from '@/lib/testIds';
import { useDebounced } from '@/lib/useDebounced';

import { AwaitingInvitationsButton } from './AwaitingInvitationsButton';
import { SupporterSummaryCards } from './SupporterSummaryCards';
import { SupportersTable } from './SupportersTable';
import { useSupporterList, useSupporterSummary } from './useSupporters';
import {
  DEFAULTS,
  TYPES,
  useSupportersParams,
  type PageSize,
  type SupporterListType,
} from './useSupportersParams';

const TITLE = 'All Supporters';
const DESCRIPTION =
  'View the people who follow your organization and the members who have joined it.';

const TYPE_LABELS: Record<SupporterListType, string> = {
  followed: 'Followed',
  invited: 'Invited',
};

/** A 404 means the organization itself cannot be read (NF3). */
const notFound = (error: unknown) => error instanceof ApiError && error.status === 404;

/**
 * The All Supporters page (PB1–PB4, PB6, PB7): four cards over the summary,
 * the filter bar, and one table over the followed or the invited list.
 *
 * The view lives in the address (P5, P14): every change goes through
 * `useSupportersParams`, and the list is keyed on what it reads back. Only the
 * search box keeps a draft of its own, settled into the address after a pause
 * in typing (P10). The cards and the list are separate queries, each with its
 * own loading and error state (P8, NF2); a 404 from either means the
 * organization cannot be read, and the page says so instead (NF3).
 */
export function SupportersPage() {
  const [params, change] = useSupportersParams();

  // What is typed, settled into the address after a pause so one request is
  // made for a word, not one per letter (scalability rule 2).
  const [draft, setDraft] = useState(params.search);
  const settled = useDebounced(draft, 300);
  useEffect(() => {
    if (settled !== params.search) change({ search: settled });
    // Only a settled draft writes the address; a changed address is read below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);
  // The address changed from outside - the back button, Clear - so the box follows.
  const [seenSearch, setSeenSearch] = useState(params.search);
  if (params.search !== seenSearch) {
    setSeenSearch(params.search);
    setDraft(params.search);
  }

  const list = useSupporterList(params);
  const summary = useSupporterSummary();

  const rows = list.data?.rows ?? [];
  const total = list.data?.pagination.total ?? 0;
  const filtered = params.search.trim() !== '';

  // A page past the end - an old link - comes back empty while the total says
  // there are rows: go to the last page there is.
  const pastTheEnd =
    list.isSuccess &&
    !list.isPlaceholderData &&
    rows.length === 0 &&
    total > 0 &&
    params.page > 1;
  useEffect(() => {
    if (pastTheEnd) change({ page: Math.max(1, Math.ceil(total / params.limit)) });
  }, [pastTheEnd, total, params.limit, change]);

  // Stable, so a keystroke in the search box does not redraw the memoised
  // table through a fresh callback (scalability rule 4).
  const { refetch } = list;
  const retry = useCallback(() => void refetch(), [refetch]);
  const turnPage = useCallback((page: number) => change({ page }), [change]);
  const changeLimit = useCallback((limit: PageSize) => change({ limit }), [change]);
  const direction = params.direction;
  const flipSort = useCallback(
    () => change({ direction: direction === 'desc' ? 'asc' : 'desc' }),
    [change, direction],
  );

  function clear() {
    setDraft('');
    change({ search: DEFAULTS.search, type: DEFAULTS.type });
  }

  const header = (
    <PageHeader
      title={TITLE}
      description={DESCRIPTION}
      action={
        <AwaitingInvitationsButton
          label="+ Invite Supporters"
          testId="supporters-invite"
        />
      }
    />
  );

  if (notFound(list.error) || notFound(summary.error)) {
    return (
      <Box data-testid="supporters-page">
        {header}
        {/* Nothing to retry: the same request would give the same answer. */}
        <ErrorState
          title="This organization was not found. It may have been removed."
          testId="supporters"
        />
      </Box>
    );
  }

  return (
    <Box data-testid="supporters-page">
      {header}

      <SupporterSummaryCards />

      <FilterBar
        testId="supporters-filters"
        searchValue={draft}
        onSearchChange={setDraft}
        searchPlaceholder="Search by name or email"
        extra={
          // Not one of the bar's own filters, which always offer "Any": the
          // two lists page separately, so there is no "All" (P1).
          <TextField
            select
            size="small"
            label="Supporter Type"
            value={params.type}
            onChange={(event) =>
              change({ type: event.target.value as SupporterListType })
            }
            slotProps={{
              select: {
                SelectDisplayProps: testIdAttr('supporters-filters-type-select'),
              },
            }}
            fullWidth={false}
            sx={{ minWidth: 170 }}
          >
            {TYPES.map((type) => (
              <MenuItem
                key={type}
                value={type}
                data-testid={`supporters-filters-type-option-${testIdPart(type)}`}
              >
                {TYPE_LABELS[type]}
              </MenuItem>
            ))}
          </TextField>
        }
        extraActive={params.type !== DEFAULTS.type}
        onClear={clear}
      />

      <SupportersTable
        rows={rows}
        type={params.type}
        loading={list.isPending}
        refreshing={list.isPlaceholderData}
        error={list.isError ? 'The supporters could not be loaded.' : null}
        onRetry={retry}
        direction={direction}
        onSortChange={flipSort}
        page={params.page}
        limit={params.limit}
        total={total}
        onPageChange={turnPage}
        onLimitChange={changeLimit}
        filtered={filtered}
      />
    </Box>
  );
}
